import React, { useState, useEffect } from 'react';
import { Users, Star, DollarSign, Clock, TrendingUp, Search, Download, Plus, Mail, Phone, MessageSquare, MapPin, Filter, Eye, Award, Zap } from 'lucide-react';
import PartnerDetail from './PartnerDetail';

const PartnersManagement = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPartnerDetail, setShowPartnerDetail] = useState(false);

  // Destinations disponibles
  const destinations = [
    { id: 'all', name: 'Toutes destinations', count: 342 },
    { id: 'st-barth', name: 'Saint-Barthélemy', count: 89 },
    { id: 'st-tropez', name: 'Saint-Tropez', count: 76 },
    { id: 'monaco', name: 'Monaco', count: 54 },
    { id: 'mykonos', name: 'Mykonos', count: 43 },
    { id: 'cannes', name: 'Cannes', count: 38 },
    { id: 'nice', name: 'Nice', count: 32 },
    { id: 'antibes', name: 'Antibes', count: 28 },
    { id: 'ibiza', name: 'Ibiza', count: 24 }
  ];

  // Statistiques du réseau
  const networkStats = {
    activePartners: 342,
    newRequests: 28,
    averageRating: 4.7,
    annualVolume: "€2.4M",
    satisfactionRate: 94,
    avgResponseTime: "12h"
  };

  // Catégories de partenaires
  const partnerCategories = [
    { id: 'all', name: 'Tous', count: 342, icon: '🌟' },
    { id: 'yacht-charter', name: 'Location Bateaux', count: 45, icon: '⛵' },
    { id: 'car-rental', name: 'Location Voitures', count: 38, icon: '🚗' },
    { id: 'private-chef', name: 'Chefs Privés', count: 52, icon: '👨‍🍳' },
    { id: 'wellness-spa', name: 'Wellness & Spa', count: 34, icon: '💆‍♀️' },
    { id: 'concierge', name: 'Conciergerie', count: 28, icon: '🎩' },
    { id: 'events', name: 'Événements', count: 31, icon: '🎉' },
    { id: 'security', name: 'Sécurité', count: 18, icon: '🛡️' },
    { id: 'transfers', name: 'Transferts', count: 42, icon: '🚁' },
    { id: 'activities', name: 'Activités', count: 54, icon: '🏄' }
  ];

  // Partenaires principaux avec données étendues
  const mainPartners = [
    {
      id: 'partner-001',
      name: 'St-Barth Yacht Charter',
      category: 'yacht-charter',
      location: 'St-Barthélemy',
      status: 'active',
      rating: 5.0,
      reviewsCount: 127,
      services: ['Day Charter', 'Sunset Cruise', 'Fishing', 'Diving'],
      volume2024: '€850K',
      bookingsCount: 89,
      responseTime: '2h',
      icon: '⛵',
      generalCommission: 15,
      contact: {
        manager: 'Captain Jean-Marc Dubois',
        email: 'jm.dubois@stbarth-yacht.com',
        phone: '+590 690 12 34 56',
        whatsapp: '+590 690 12 34 56'
      },
      address: {
        street: 'Quai du Yacht Club',
        city: 'Gustavia',
        postalCode: '97133',
        country: 'Saint-Barthélemy'
      },
      businessHours: {
        monday: { open: '08:00', close: '18:00', closed: false },
        tuesday: { open: '08:00', close: '18:00', closed: false },
        wednesday: { open: '08:00', close: '18:00', closed: false },
        thursday: { open: '08:00', close: '18:00', closed: false },
        friday: { open: '08:00', close: '18:00', closed: false },
        saturday: { open: '09:00', close: '17:00', closed: false },
        sunday: { open: '09:00', close: '17:00', closed: false }
      },
      bankingInfo: {
        rib: 'FR76 3000 4000 1100 0001 2345 67',
        bankName: 'Crédit Agricole Antilles-Guyane',
        accountHolder: 'St-Barth Yacht Charter SARL'
      }
    },
    {
      id: 'partner-002',
      name: 'Chef Laurent Dubois',
      category: 'private-chef',
      location: 'St-Tropez',
      status: 'active',
      rating: 5.0,
      reviewsCount: 94,
      services: ['Gastronomique', 'Méditerranéen', 'Végétarien', 'Sans gluten'],
      volume2024: '€420K',
      bookingsCount: 156,
      responseTime: '30min',
      icon: '👨‍🍳',
      generalCommission: 12,
      contact: {
        manager: 'Laurent Dubois',
        email: 'laurent@chef-dubois.com',
        phone: '+33 6 12 34 56 78',
        whatsapp: '+33 6 12 34 56 78'
      },
      address: {
        street: '15 Avenue des Palmiers',
        city: 'Saint-Tropez',
        postalCode: '83990',
        country: 'France'
      },
      businessHours: {
        monday: { open: '10:00', close: '22:00', closed: false },
        tuesday: { open: '10:00', close: '22:00', closed: false },
        wednesday: { open: '10:00', close: '22:00', closed: false },
        thursday: { open: '10:00', close: '22:00', closed: false },
        friday: { open: '10:00', close: '22:00', closed: false },
        saturday: { open: '10:00', close: '23:00', closed: false },
        sunday: { open: '10:00', close: '22:00', closed: false }
      },
      bankingInfo: {
        rib: 'FR76 3000 4000 1100 0001 2345 68',
        bankName: 'BNP Paribas',
        accountHolder: 'Laurent Dubois Chef EURL'
      }
    },
    {
      id: 'partner-003',
      name: 'Riviera Luxury Cars',
      category: 'car-rental',
      location: 'Monaco',
      status: 'active',
      rating: 5.0,
      reviewsCount: 203,
      services: ['Ferrari', 'Lamborghini', 'Rolls-Royce', 'Bentley'],
      volume2024: '€1.2M',
      bookingsCount: 234,
      responseTime: '1h',
      icon: '🚗',
      generalCommission: 18,
      contact: {
        manager: 'Alessandro Ferrari',
        email: 'alessandro@riviera-luxury.mc',
        phone: '+377 98 06 12 34',
        whatsapp: '+377 98 06 12 34'
      },
      address: {
        street: '12 Boulevard des Moulins',
        city: 'Monte-Carlo',
        postalCode: '98000',
        country: 'Monaco'
      },
      businessHours: {
        monday: { open: '09:00', close: '19:00', closed: false },
        tuesday: { open: '09:00', close: '19:00', closed: false },
        wednesday: { open: '09:00', close: '19:00', closed: false },
        thursday: { open: '09:00', close: '19:00', closed: false },
        friday: { open: '09:00', close: '19:00', closed: false },
        saturday: { open: '10:00', close: '18:00', closed: false },
        sunday: { open: '10:00', close: '17:00', closed: false }
      },
      bankingInfo: {
        rib: 'MC58 1273 9000 7000 1234 5678 901',
        bankName: 'Crédit Agricole de Monaco',
        accountHolder: 'Riviera Luxury Cars SAM'
      }
    },
    {
      id: 'partner-004',
      name: 'Zen Wellness Mykonos',
      category: 'wellness-spa',
      location: 'Mykonos',
      status: 'active',
      rating: 5.0,
      reviewsCount: 178,
      services: ['Massage', 'Yoga', 'Meditation', 'Beauty'],
      volume2024: '€380K',
      bookingsCount: 412,
      responseTime: '4h',
      icon: '💆‍♀️',
      generalCommission: 20,
      contact: {
        manager: 'Maria Papadopoulos',
        email: 'maria@zen-mykonos.gr',
        phone: '+30 694 123 4567',
        whatsapp: '+30 694 123 4567'
      },
      address: {
        street: 'Waterfront Promenade',
        city: 'Mykonos Town',
        postalCode: '84600',
        country: 'Greece'
      },
      businessHours: {
        monday: { open: '09:00', close: '21:00', closed: false },
        tuesday: { open: '09:00', close: '21:00', closed: false },
        wednesday: { open: '09:00', close: '21:00', closed: false },
        thursday: { open: '09:00', close: '21:00', closed: false },
        friday: { open: '09:00', close: '21:00', closed: false },
        saturday: { open: '10:00', close: '22:00', closed: false },
        sunday: { open: '10:00', close: '20:00', closed: false }
      },
      bankingInfo: {
        rib: 'GR16 0110 1250 0000 0001 2345 67',
        bankName: 'National Bank of Greece',
        accountHolder: 'Zen Wellness Mykonos Ltd'
      }
    },
    {
      id: 'partner-005',
      name: 'Héli Riviera',
      category: 'transfers',
      location: 'Nice',
      status: 'active',
      rating: 5.0,
      reviewsCount: 89,
      services: ['Airport Transfer', 'Scenic Tours', 'Events'],
      volume2024: '€650K',
      bookingsCount: 156,
      responseTime: '30min',
      icon: '🚁',
      generalCommission: 10,
      contact: {
        manager: 'Pierre Moreau',
        email: 'pierre@heli-riviera.fr',
        phone: '+33 4 93 21 30 30',
        whatsapp: '+33 6 12 34 56 89'
      },
      address: {
        street: 'Aéroport Nice Côte d\'Azur',
        city: 'Nice',
        postalCode: '06206',
        country: 'France'
      },
      businessHours: {
        monday: { open: '07:00', close: '20:00', closed: false },
        tuesday: { open: '07:00', close: '20:00', closed: false },
        wednesday: { open: '07:00', close: '20:00', closed: false },
        thursday: { open: '07:00', close: '20:00', closed: false },
        friday: { open: '07:00', close: '20:00', closed: false },
        saturday: { open: '08:00', close: '19:00', closed: false },
        sunday: { open: '08:00', close: '19:00', closed: false }
      },
      bankingInfo: {
        rib: 'FR76 3000 4000 1100 0001 2345 69',
        bankName: 'Société Générale',
        accountHolder: 'Héli Riviera SAS'
      }
    },
    {
      id: 'partner-006',
      name: 'Elite Protection Services',
      category: 'security',
      location: 'St-Barth',
      status: 'pending',
      rating: 5.0,
      reviewsCount: 45,
      services: ['Bodyguard', 'Villa Security', 'Event Security'],
      volume2024: '€320K',
      bookingsCount: 67,
      responseTime: '15min',
      icon: '🛡️',
      generalCommission: 8,
      contact: {
        manager: 'Marcus Thompson',
        email: 'marcus@elite-protection.sb',
        phone: '+590 690 98 76 54',
        whatsapp: '+590 690 98 76 54'
      },
      address: {
        street: 'Rue de la République',
        city: 'Gustavia',
        postalCode: '97133',
        country: 'Saint-Barthélemy'
      },
      businessHours: {
        monday: { open: '00:00', close: '23:59', closed: false },
        tuesday: { open: '00:00', close: '23:59', closed: false },
        wednesday: { open: '00:00', close: '23:59', closed: false },
        thursday: { open: '00:00', close: '23:59', closed: false },
        friday: { open: '00:00', close: '23:59', closed: false },
        saturday: { open: '00:00', close: '23:59', closed: false },
        sunday: { open: '00:00', close: '23:59', closed: false }
      },
      bankingInfo: {
        rib: 'FR76 3000 4000 1100 0001 2345 70',
        bankName: 'Crédit Agricole Antilles-Guyane',
        accountHolder: 'Elite Protection Services SARL'
      }
    }
  ];

  // Top performers du mois
  const topPerformers = [
    {
      name: 'St-Barth Yacht Charter',
      satisfaction: 95,
      monthlyRevenue: '€125K'
    },
    {
      name: 'Chef Laurent Dubois',
      satisfaction: 92,
      monthlyRevenue: '€48K'
    },
    {
      name: 'Riviera Luxury Cars',
      satisfaction: 89,
      monthlyRevenue: '€187K'
    },
    {
      name: 'Zen Wellness Mykonos',
      satisfaction: 94,
      monthlyRevenue: '€42K'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={14}
        className={i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
      />
    ));
  };

  const filteredPartners = mainPartners.filter(partner => {
    const matchesCategory = selectedCategory === 'all' || partner.category === selectedCategory;
    const matchesSearch = partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         partner.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDestination = selectedDestination === 'all' || 
                              partner.location.toLowerCase().includes(
                                destinations.find(d => d.id === selectedDestination)?.name.toLowerCase() || ''
                              );
    return matchesCategory && matchesSearch && matchesDestination;
  });

  const PartnerCard = ({ partner }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-2xl">
            {partner.icon}
          </div>
          <div>
            <h3 
              className="font-semibold text-gray-800 hover:text-blue-600 cursor-pointer transition-colors"
              onClick={() => {
                setSelectedPartner(partner);
                setShowPartnerDetail(true);
              }}
            >
              {partner.name}
            </h3>
            <p className="text-sm text-gray-600">{partnerCategories.find(c => c.id === partner.category)?.name}</p>
            <div className="flex items-center space-x-1 mt-1">
              <MapPin size={12} className="text-gray-400" />
              <span className="text-xs text-gray-500">{partner.location}</span>
            </div>
          </div>
        </div>
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(partner.status)}`}>
          {partner.status === 'active' ? 'Actif' : partner.status === 'pending' ? 'En attente' : 'Inactif'}
        </span>
      </div>

      <div className="flex items-center space-x-1 mb-3">
        {getRatingStars(partner.rating)}
        <span className="text-sm text-gray-600 ml-2">({partner.reviewsCount} avis)</span>
      </div>

      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Services</h4>
        <div className="flex flex-wrap gap-1">
          {partner.services.slice(0, 4).map((service, index) => (
            <span key={index} className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
              {service}
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Volume 2024</span>
          <span className="font-medium text-green-600">{partner.volume2024}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Réservations</span>
          <span className="font-medium">{partner.bookingsCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Réponse</span>
          <span className="font-medium">{partner.responseTime}</span>
        </div>
      </div>

      <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-gray-200">
        <button className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50">
          <Phone size={14} />
          <span>Appeler</span>
        </button>
        <button className="flex items-center space-x-1 px-3 py-1 text-sm text-green-600 border border-green-300 rounded hover:bg-green-50">
          <MessageSquare size={14} />
          <span>WhatsApp</span>
        </button>
        <button className="flex items-center space-x-1 px-3 py-1 text-sm text-purple-600 border border-purple-300 rounded hover:bg-purple-50">
          <Mail size={14} />
          <span>Email</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="partners-management">
      {showPartnerDetail ? (
        <PartnerDetail 
          partner={selectedPartner} 
          onBack={() => {
            setShowPartnerDetail(false);
            setSelectedPartner(null);
          }} 
        />
      ) : (
        <>
          {/* En-tête */}
          <div className="page-header bg-white border-b border-gray-200 p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Réseau de Partenaires & Prestataires</h1>
                <p className="text-gray-600 mt-1">Gérez vos partenaires de services premium pour villas et yachts de luxe</p>
              </div>
              
              <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Filter size={18} />
              <span>Filtres</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Download size={18} />
              <span>Export</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouveau Partenaire</span>
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-blue-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{networkStats.activePartners}</div>
            <div className="text-sm text-gray-600">Partenaires Actifs</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Plus className="text-yellow-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{networkStats.newRequests}</div>
            <div className="text-sm text-gray-600">Nouvelles Demandes</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="text-orange-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{networkStats.averageRating}/5</div>
            <div className="text-sm text-gray-600">Note Moyenne</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-green-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{networkStats.annualVolume}</div>
            <div className="text-sm text-gray-600">Volume Annuel</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-purple-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{networkStats.satisfactionRate}%</div>
            <div className="text-sm text-gray-600">Taux Satisfaction</div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-teal-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{networkStats.avgResponseTime}</div>
            <div className="text-sm text-gray-600">Temps Réponse Moy.</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filtre par Destination - Mis en évidence */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                <MapPin className="text-yellow-600" size={20} />
                <span>Filtrer par Destination</span>
              </h3>
              <p className="text-sm text-gray-600 mt-1">Sélectionnez une destination pour afficher les partenaires locaux</p>
            </div>
            <div className="text-sm text-gray-500">
              {filteredPartners.length} partenaire(s) trouvé(s)
            </div>
          </div>
          
          <div className="relative max-w-md">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-yellow-600" size={16} />
            <select
              value={selectedDestination}
              onChange={(e) => setSelectedDestination(e.target.value)}
              className="pl-10 pr-8 py-3 w-full border border-yellow-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 font-medium"
            >
              {destinations.map((destination) => (
                <option key={destination.id} value={destination.id}>
                  {destination.name} ({destination.count})
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Filtres et Recherche */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap gap-2">
              {partnerCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{category.icon}</span>
                  <span>{category.name}</span>
                  <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                    {category.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher un partenaire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
          </div>
        </div>

        {/* Grille des Partenaires */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {filteredPartners.map((partner) => (
            <PartnerCard key={partner.id} partner={partner} />
          ))}
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <Award className="text-yellow-600" size={20} />
              <span>Top Performers du Mois</span>
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-700">Voir Détails</button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {topPerformers.map((performer, index) => (
              <div key={index} className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">{performer.name}</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Satisfaction</span>
                    <span className="font-medium text-green-600">{performer.satisfaction}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ce mois</span>
                    <span className="font-medium text-blue-600">{performer.monthlyRevenue}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default PartnersManagement;